import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { store } from 'Reducers';
import logger from 'Utils/Logger';
import { getCurrentAuthToken } from 'Utils/Auth';
import { reportError } from 'Utils/reportError';
import { getPlatformInfo, getDeviceClass, getViewport } from 'Actions/events';
import { EventType, PhraseNoteError } from 'Types/EventTypes';
import type EventStream from './EventStream';

// MUS-499: play-time per-note flush controller.
//
// Per-note rows are captured AS PLAYED (they already accumulate in the phrase's
// playData on the tick loop) and flushed asynchronously in batches, so a phrase the
// user stops on / half-plays / navigates away from no longer drops its notes.
//
// This is deliberately self-contained (its own transport, not the shared eventQueue)
// so it can (a) run its own 2s backstop cadence, and (b) use a keepalive/beacon send
// on teardown - without changing how any other event flushes.
//
// Triggers (wired by the caller):
//   - a 2s timer (crash/OOM backstop; most notes flush earlier on a boundary/pause)
//   - phrase boundary + pause          -> flush('boundary' | 'pause'), alive path
//   - visibility-hidden / app-inactive / pagehide -> flushOnTeardown(), keepalive path
//
// Idempotency: each flushed batch is one event carrying a client_event_id stamped
// ONCE when the batch is cut and stable across any resend (a network retry, or a
// teardown beacon re-sending an un-acked batch). The backend dedups on it via the
// shared event_dedup ledger (MUS-418). Complementarily, each note is serialized
// exactly once (the Flushed high-water mark on NoteEvent), so batches don't overlap
// under normal play - dedup only has to catch a true redelivery.
//
// Retry: a batch is retained and resent only while a resend can succeed - no
// response (network error), a 5xx, or a retryable 4xx. Any other 4xx is a permanent
// reject: the server would answer a resend the same way, so the POST's batches are
// dropped and reported once instead of being resent on every flush with each new
// batch piled on.

const FLUSH_INTERVAL_MS = 2000;

// 4xx statuses that mean "try again" rather than "this request is wrong": an auth
// token the interceptor could not refresh in time, a request timeout, a rate limit.
const RETRYABLE_CLIENT_ERROR_STATUSES = [401, 408, 429];

const isPermanentReject = (status: number): boolean =>
  status >= 400 && status < 500 && !RETRYABLE_CLIENT_ERROR_STATUSES.includes(status);

// The slice of the events endpoint's partial-success summary this controller reads.
// A 422 carries it, with one failed[] entry per event the server could not persist.
// A 200 carries it too, with one skipped.invalid[] entry per event that failed
// validation and was not persisted.
type EventsPostSummary = {
  skipped?: { invalid?: Array<{ reason?: unknown }> };
  failed?: Array<{ reason?: unknown }>;
};

type FlushContext = {
  playSessionId: string;
  levelNumber: number;
  tierNumber: number;
  errorRecognitionMode: string; // 'MIDI' | 'AUDIO'
};

// One flushed play-time event: the wire payload plus its stable client_event_id.
type PendingBatch = {
  client_event_id: string;
  event: Record<string, unknown>;
};

export class PlayTimeNoteFlushController {
  // Pulls the resolved-unflushed rows grouped per phrase from the active stream.
  private getBatches:
    | ((lookback?: number) => { phraseS3Id: string; notes: PhraseNoteError[] }[])
    | null = null;
  // MUS-473: optional best-effort snapshot of the in-progress phrase at teardown.
  private getInterruptionSnapshot:
    | (() => { phraseS3Id?: string; position?: number } | null)
    | null = null;
  private context: FlushContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  // Un-acked flushed batches, keyed by client_event_id. Retained until a 2xx or a
  // permanent reject, then dropped. Re-sent on teardown / next flush with the SAME id
  // so a redelivery dedups.
  private pending: Map<string, PendingBatch> = new Map();
  private sending = false;
  // MUS-473: stable-per-INTERRUPTION-EPISODE id for the teardown SessionInterruption
  // event. One teardown fires flushOnTeardown() 2-3x (visibilitychange->hidden,
  // pagehide, and appStateChange->inactive on mobile); those share this id so the
  // MUS-418 backend dedup collapses them to a single interruption row. A foreground-
  // return (resetInterruptionEpisode) clears it, so a LATER background within the same
  // play session mints a fresh id and records as its OWN distinct interruption at its
  // own phrase - each genuine leave-point is preserved, rather than first-write-wins
  // freezing the whole session at the earliest one. Null until the first teardown of
  // an episode mints one; reset on foreground-return, session (re)configure, and stop.
  private interruptionClientEventId: string | null = null;

  private get eventsUrl(): string {
    return `${process.env.REACT_APP_BACKEND_URL}/api/v1/events`;
  }

  private get enabled(): boolean {
    return process.env.REACT_APP_SEND_EVENTS === 'true';
  }

  // Called when a play session starts. Idempotent: reconfiguring restarts the timer.
  configure(
    getBatches: (lookback?: number) => { phraseS3Id: string; notes: PhraseNoteError[] }[],
    context: FlushContext,
    getInterruptionSnapshot?: () => { phraseS3Id?: string; position?: number } | null,
  ) {
    this.getBatches = getBatches;
    this.context = context;
    this.getInterruptionSnapshot = getInterruptionSnapshot ?? null;
    // A new session starts a fresh interruption episode.
    this.interruptionClientEventId = null;
    this.startTimer();
  }

  private startTimer() {
    this.stopTimer();
    this.timer = setInterval(() => {
      this.flush('timer').catch((err) => logger.warn('[play-time-flush] timer flush failed', err));
    }, FLUSH_INTERVAL_MS);
  }

  private stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // Called when a play session ends. Does a final alive flush, then detaches. Pending
  // batches survive the detach so a straggler retry (or teardown) can still send them.
  async stop() {
    this.stopTimer();
    await this.flush('stop').catch((err) =>
      logger.warn('[play-time-flush] stop flush failed', err),
    );
    this.getBatches = null;
    this.context = null;
    this.getInterruptionSnapshot = null;
    this.interruptionClientEventId = null;
  }

  // MUS-473: called when the app returns to the foreground (tab visible / app active).
  // Ends the current interruption episode so the NEXT teardown mints a fresh id. A
  // single teardown's 2-3 duplicate fires still share one id (they happen before any
  // return); a genuine background-then-return-then-background sequence records as two
  // distinct interruptions, each at its own in-progress phrase.
  resetInterruptionEpisode() {
    this.interruptionClientEventId = null;
  }

  private buildBase(): Record<string, unknown> {
    const sessionId = store.getState()?.eventReducer?.sessionId ?? '';
    return {
      datetime: new Date().toISOString(),
      analytics_session_id: sessionId,
      platform: getPlatformInfo(),
      device_class: getDeviceClass(),
      ...getViewport(),
    };
  }

  // Pull unflushed rows, cut one batch per phrase (each with its own client_event_id),
  // and add them to the pending set. Returns whether anything new was captured.
  private capture(lookback?: number): boolean {
    if (!this.getBatches || !this.context) return false;
    const groups = this.getBatches(lookback);
    if (!groups.length) return false;
    const base = this.buildBase();
    for (const group of groups) {
      const clientEventId = uuidv4();
      this.pending.set(clientEventId, {
        client_event_id: clientEventId,
        event: {
          ...base,
          client_event_id: clientEventId,
          event_type: EventType.PlayTimeNotesEvent,
          phrase_s3_id: group.phraseS3Id,
          level_number: this.context.levelNumber,
          tier_number: this.context.tierNumber,
          play_session_id: this.context.playSessionId,
          error_recognition_mode: this.context.errorRecognitionMode,
          notes: group.notes,
        },
      });
    }
    return true;
  }

  // MUS-378 RUM path: one error per permanently rejected POST, under the same
  // 'events.post' tag the shared queue reports its rejected entries with.
  private reportDroppedBatches(dropped: PendingBatch[], httpStatus: number, err: unknown) {
    const summary = axios.isAxiosError(err)
      ? (err.response?.data as EventsPostSummary | undefined)
      : undefined;
    const failed = summary?.failed ?? [];
    const firstReason = failed[0]?.reason;
    const detail = {
      httpStatus,
      droppedBatches: dropped.length,
      droppedNotes: dropped.reduce(
        (total, b) => total + (Array.isArray(b.event.notes) ? b.event.notes.length : 0),
        0,
      ),
      failedCount: failed.length,
      firstFailureReason: typeof firstReason === 'string' ? firstReason.slice(0, 120) : undefined,
    };
    reportError(
      new Error(`play-time notes POST rejected, batches dropped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // One error per accepted POST whose summary lists events skipped as invalid. The
  // server did not persist them and would skip a resend the same way, so they stay
  // acked; without this report the loss is silent on both sides.
  private reportInvalidSkips(
    summary: EventsPostSummary | undefined,
    httpStatus: number | undefined,
    sentBatches: number,
  ) {
    const invalid = summary?.skipped?.invalid ?? [];
    if (invalid.length === 0) return;
    const firstReason = invalid[0]?.reason;
    const detail = {
      httpStatus,
      sentBatches,
      invalidCount: invalid.length,
      firstInvalidReason:
        firstReason === undefined ? undefined : JSON.stringify(firstReason).slice(0, 120),
    };
    reportError(
      new Error(`play-time notes POST accepted, invalid batches skipped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // Alive-path flush (timer / boundary / pause / stop): capture, then POST all
  // pending batches over the authenticated axios path (Bearer attached by the
  // interceptor). On 2xx, drop the batches that were sent, reporting any the server
  // skipped as invalid. On a permanent reject, drop and report them. Any other
  // failure keeps them for the next flush.
  async flush(_reason: string): Promise<void> {
    if (!this.enabled) return;
    this.capture();
    if (this.pending.size === 0 || this.sending) return;
    this.sending = true;
    const inFlight = [...this.pending.values()];
    try {
      const response = await axios.post<EventsPostSummary>(
        this.eventsUrl,
        inFlight.map((b) => b.event),
      );
      for (const b of inFlight) this.pending.delete(b.client_event_id);
      this.reportInvalidSkips(response?.data, response?.status, inFlight.length);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status !== undefined && isPermanentReject(status)) {
        // The server would answer a resend the same way, and on the events
        // endpoint's own 422 it has already persisted every event it could. Nothing
        // in this POST is worth keeping: a retained batch would be resent on every
        // flush for the rest of the page's life.
        for (const b of inFlight) this.pending.delete(b.client_event_id);
        this.reportDroppedBatches(inFlight, status, err);
      } else {
        // Keep the batches pending; the next flush (or teardown) resends them. The
        // stable client_event_id means the resend dedups if the first actually landed.
        logger.warn('[play-time-flush] alive flush failed; retaining for retry', err);
      }
    } finally {
      this.sending = false;
    }
  }

  // Teardown-path flush (page hidden / app inactive / pagehide / unload). Captures a
  // wider lookback for safety, then sends all pending batches with a transport that
  // survives teardown: keepalive fetch (carries the Authorization header, so it hits
  // the same auth-guarded endpoint), falling back to sendBeacon only where fetch
  // keepalive is unavailable. Best-effort and non-blocking; pending is NOT cleared
  // (the page is going away - if it survives, the next alive flush resends and the
  // backend dedups).
  async flushOnTeardown(): Promise<void> {
    if (!this.enabled) return;
    this.capture(12);
    // MUS-473: a page teardown during play is an interruption. Emit one
    // SessionInterruptionEvent (its own client_event_id) alongside any pending
    // note batches, on the same keepalive/beacon path. Sent even when no notes
    // are pending, so a mid-phrase reload after the last flush still records it.
    const outgoing: Record<string, unknown>[] = [];
    if (this.context) {
      const snap = this.getInterruptionSnapshot?.() ?? null;
      // Mint ONCE per interruption episode (see the field doc), then reuse across the
      // 2-3 duplicate teardown fires so they dedup to one row. resetInterruptionEpisode
      // (foreground-return) / configure (new session) / stop clear it.
      this.interruptionClientEventId ??= uuidv4();
      const interruptionClientEventId = this.interruptionClientEventId;
      outgoing.push({
        ...this.buildBase(),
        client_event_id: interruptionClientEventId,
        event_type: EventType.SessionInterruptionEvent,
        play_session_id: this.context.playSessionId,
        level_number: this.context.levelNumber,
        tier_number: this.context.tierNumber,
        phrase_s3_id: snap?.phraseS3Id,
        position: snap?.position,
      });
    }
    for (const b of this.pending.values()) outgoing.push(b.event);
    if (outgoing.length === 0) return;
    const body = JSON.stringify(outgoing);

    const supportsKeepalive = typeof fetch === 'function';
    if (supportsKeepalive) {
      let token: string | undefined;
      try {
        token = await getCurrentAuthToken();
      } catch {
        token = undefined;
      }
      try {
        await fetch(this.eventsUrl, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body,
        });
        return;
      } catch (err) {
        logger.warn('[play-time-flush] keepalive fetch failed', err);
      }
    }

    // Last resort: sendBeacon cannot set an Authorization header, so this only lands
    // if the endpoint can accept it; used solely where keepalive fetch is unavailable.
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(this.eventsUrl, new Blob([body], { type: 'application/json' }));
      }
    } catch (err) {
      logger.warn('[play-time-flush] sendBeacon fallback failed', err);
    }
  }
}

// MUS-473: the teardown interruption snapshot the play screen configures. It
// reads the phrase in progress (and the position inside it) off the live event
// stream. While the stream holds no phrase - before the first is laid out, or
// after a session or tier reset empties it - the event carries no phrase rather
// than a stale one.
export const phraseInProgressSnapshot =
  (stream: Pick<EventStream, 'getPhraseInProgress'>) =>
  (): { phraseS3Id?: string; position?: number } | null =>
    stream.getPhraseInProgress() ?? null;

// Singleton - one active play session at a time.
export const playTimeNoteFlushController = new PlayTimeNoteFlushController();
